import React, { PropsWithChildren, ReactNode } from 'react';
import { cn } from '@helpers/className';
import { getSlot, Slot } from '@components/common/Slot';

export interface GalleryProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}
export const HotelCardGallery = React.forwardRef<HTMLDivElement, GalleryProps>(
  ({ asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp {...props} ref={ref}>
        {children}
      </Comp>
    );
  },
);
HotelCardGallery.displayName = 'HotelCard.Gallery';

export interface TitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}

export const HotelCardTitle = React.forwardRef<HTMLHeadingElement, TitleProps>(
  ({ asChild = false, children, className, ...props }, ref) => {
    const cls = cn(
      'text-base desktop:text-lg font-semibold leading-6 text-left whitespace-nowrap overflow-hidden overflow-ellipsis desktop:max-w-md',
      className,
    );
    const Comp = asChild ? Slot : 'h2';
    return (
      <Comp ref={ref} className={cls} {...props}>
        {children}
      </Comp>
    );
  },
);

HotelCardTitle.displayName = 'HotelCard.Title';

export interface HotelCardFavoritesTriggerProps extends React.HTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}

export const HotelCardFavoritesTrigger = React.forwardRef<
HTMLButtonElement,
HotelCardFavoritesTriggerProps
>(({ asChild = false, children, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp ref={ref} {...props}>
      {children}
    </Comp>
  );
});

HotelCardFavoritesTrigger.displayName = 'HotelCard.FavoritesTrigger';

export interface HotelCardRatingProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}

export const HotelCardRating = React.forwardRef<HTMLDivElement, HotelCardRatingProps>(
  ({ asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';

    return (
      <Comp ref={ref} {...props}>
        {children}
      </Comp>
    );
  },
);

HotelCardRating.displayName = 'HotelCard.Rating';

export interface HotelCardLocationProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}

export const HotelCardLocation = React.forwardRef<HTMLDivElement, HotelCardLocationProps>(
  ({ asChild = false, children, className, ...props }, ref) => {
    const cls = cn(
      'text-sm text-rp-text-secondary whitespace-nowrap overflow-hidden overflow-ellipsis',
      className,
    );
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp ref={ref} className={cls} {...props}>
        {children}
      </Comp>
    );
  },
);

HotelCardLocation.displayName = 'HotelCard.Location';

export interface HotelCardVibesProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}

export const HotelCardVibes = React.forwardRef<HTMLDivElement, HotelCardVibesProps>(
  ({ asChild = false, children, className, ...props }, ref) => {
    const cls = cn('text-sm text-rp-text-secondary', className);
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp ref={ref} className={cls} {...props}>
        {children}
      </Comp>
    );
  },
);

HotelCardVibes.displayName = 'HotelCard.Vibes';

export interface HotelCardAmenitiesProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}
export const HotelCardAmenities = React.forwardRef<HTMLDivElement, HotelCardAmenitiesProps>(
  ({ asChild = false, children, className, ...props }, ref) => {
    const cls = cn('overflow-hidden desktop:overflow-visible relative', className);
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp ref={ref} className={cls} {...props}>
        {children}
      </Comp>
    );
  },
);
HotelCardAmenities.displayName = 'HotelCard.Amenities';

export interface HotelCardProductsProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}
export const HotelCardProducts = React.forwardRef<HTMLDivElement, HotelCardProductsProps>(
  ({ asChild, children, className, ...props }, ref) => {
    const cls = cn('', className);
    const Comp = asChild ? Slot : 'div';
    const cmpProps = cls === '' ? props : { ...props, className: cls };

    return (
      <Comp ref={ref} {...cmpProps}>
        {children}
      </Comp>
    );
  },
);
HotelCardProducts.displayName = 'HotelCard.Products';

export interface HotelCardExperiencesProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}

export const HotelCardExperiences = React.forwardRef<HTMLDivElement, HotelCardExperiencesProps>(
  ({ asChild, children, className, ...props }, ref) => {
    const cls = cn('text-solar-secondary', className);
    const Comp = asChild ? Slot : 'p';
    return (
      <Comp ref={ref} className={cls} {...props}>
        {children}
      </Comp>
    );
  },
);
HotelCardExperiences.displayName = 'HotelCard.Experiences';

export interface HotelCardDetailsTriggerProps extends React.HTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}

export const HotelCardDetailsTrigger = React.forwardRef<
HTMLButtonElement,
HotelCardDetailsTriggerProps
>(({ asChild, children, className, ...props }, ref) => {
  const cls = cn('', className);
  const Comp = asChild ? Slot : 'button';
  const cmpProps = cls === '' ? props : { ...props, className: cls };

  return (
    <Comp ref={ref} {...cmpProps}>
      {children}
    </Comp>
  );
});

export interface HotelCardCancellationPolicyProps
  extends React.HTMLAttributes<HTMLParagraphElement> {
  asChild?: boolean;
  className?: string;
  children: ReactNode;
}
export const HotelCardCancellationPolicy = React.forwardRef<
HTMLParagraphElement,
HotelCardCancellationPolicyProps
>(({ asChild, className, children, ...props }, ref) => {
  const cls = cn('', className);
  const Comp = asChild ? Slot : 'p';
  const cmpProps = cls === '' ? props : { ...props, className: cls };

  return (
    <Comp ref={ref} {...cmpProps}>
      {children}
    </Comp>
  );
});
HotelCardCancellationPolicy.displayName = 'HotelCard.CancellationPolicy';

HotelCardDetailsTrigger.displayName = 'HotelCard.DetailsTrigger';

export interface HotelCardProps {
  className?: string;
  highlight?: boolean;
}
export function HotelCard({ className, children, highlight }: HotelCardProps & PropsWithChildren) {
  const cls = cn(
    'isolate relative w-full pointer-events-none',
    highlight &&
      'box-border rounded-none desktop:box-content static left-0 p-0 desktop:relative desktop:-left-10px desktop:p-2 bg-rp-surface-brand-daylight border-solid border-2 desktop:rounded border-rp-surface-alert-warning pb-8 desktop:pb-2',
    className,
  );
  const items = React.Children.toArray(children);

  const gallery = getSlot(items, HotelCardGallery);
  const title = getSlot(items, HotelCardTitle);
  const favTrigger = getSlot(items, HotelCardFavoritesTrigger);
  const rating = getSlot(items, HotelCardRating);
  const location = getSlot(items, HotelCardLocation);
  const vibes = getSlot(items, HotelCardVibes);
  const amenities = getSlot(items, HotelCardAmenities);
  const products = getSlot(items, HotelCardProducts);
  const experiences = getSlot(items, HotelCardExperiences);
  const cancellationPolicy = getSlot(items, HotelCardCancellationPolicy);
  const detailsTrigger = getSlot(items, HotelCardDetailsTrigger);

  return (
    <div className={cls}>
      <div className="flex flex-col desktop:flex-row gap-4 desktop:gap-6">
        {gallery}
        <div className="flex flex-col gap-4 desktop:gap-6 w-full px-4 desktop:px-0 pointer-events-auto">
          <div className="flex flex-col gap-1 w-full">
            <div className="flex flex-row justify-between items-center gap-1">
              {title}
              {favTrigger}
            </div>
            <div className="flex flex-row gap-1 items-center mb-spacing-xs">
              {rating && (
                <div className="flex flex-row items-center gap-1">
                  {rating}
                  <div className="text-sm font-rp-basetica text-rp-text-secondary">|</div>
                </div>
              )}
              {location}
              {vibes && (
                <>
                  <div className="text-sm text-rp-text-secondary">|</div>
                  {vibes}
                </>
              )}
            </div>
            {amenities}
          </div>
          {products}
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-col gap-1 text-xs leading-14px">
              {experiences}
              {cancellationPolicy}
            </div>
            <div className="w-107px">{detailsTrigger}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

HotelCard.Gallery = HotelCardGallery;
HotelCard.Title = HotelCardTitle;
HotelCard.FavoritesTrigger = HotelCardFavoritesTrigger;
HotelCard.Rating = HotelCardRating;
HotelCard.Location = HotelCardLocation;
HotelCard.Vibes = HotelCardVibes;
HotelCard.Amenities = HotelCardAmenities;
HotelCard.Products = HotelCardProducts;
HotelCard.Experiences = HotelCardExperiences;
HotelCard.DetailsTrigger = HotelCardDetailsTrigger;
HotelCard.CancellationPolicy = HotelCardCancellationPolicy;
